import { onMounted, ref, watch } from 'vue';
import { useRoute } from 'vue-router';

const resolveModeFromRoute = (
  routeName: string | null | undefined,
  routeId: unknown
) => {
  const name = routeName?.toString() ?? '';
  const numericId = Number(routeId);
  const hasId = Number.isFinite(numericId) && numericId > 0;

  const isDetail = name.includes('chi-tiet') || name.includes('detail');

  const isEdit =
    name.includes('chinh-sua') ||
    name.includes('cap-nhat') ||
    (hasId && !name.includes('tao-moi') && !name.includes('create'));

  return {
    isCreating: !isEdit && !isDetail,
    isDetail,
    id: hasId && (isEdit || isDetail) ? numericId : 0,
  };
};

/**
 * Shared create/edit/detail form mode derived from the current route.
 *
 * Must stay in sync when create/update pages reuse the same component instance
 * (table row actions navigate create ↔ edit or between edit ids).
 *
 * Refs stay writable for callers that override mode locally.
 */
export const useCreatingForm = (idString = 'id') => {
  const route = useRoute();
  const isCreating = ref<boolean>(true);
  const isDetail = ref<boolean>(false);
  const id = ref(0);
  const loading = ref(false);
  const formRef = ref<null | HTMLFormElement>(null);

  const syncFromRoute = () => {
    const next = resolveModeFromRoute(
      route.name?.toString(),
      route.params[idString]
    );

    isCreating.value = next.isCreating;
    isDetail.value = next.isDetail;
    id.value = next.id;
  };

  // Keep mode reactive for in-place route changes (same component reused).
  watch(
    () => [route.name, route.params[idString]] as const,
    () => {
      syncFromRoute();
    },
    { immediate: true }
  );

  // Preserve previous onMounted timing for first paint edge cases.
  onMounted(() => {
    syncFromRoute();
  });

  return {
    isCreating,
    isDetail,
    id,
    loading,
    formRef,
  };
};
